import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  getPropertyForestContext,
  type PropertyForestContext,
} from "@/api/propertyForestContext";
import { logger } from "@/utils/debugLogger";
import { storyQueryKeys } from "@/queries/storyQueryKeys";

interface UsePropertyForestContextResult {
  context: PropertyForestContext | null;
  isLoading: boolean;
  isStale: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface PropertyForestContextQueryOptions {
  refetchOnMount?: boolean | "always";
  /** Public showcase read: no bearer, so a logged-in non-owner still gets geometry. */
  omitAuthorization?: boolean;
}

export function usePropertyForestContext(
  propertyId: string | null | undefined,
  bufferMeters: number = 50,
  enabled: boolean = true,
  options: PropertyForestContextQueryOptions = {},
): UsePropertyForestContextResult {
  const query = useQuery<PropertyForestContext, Error>({
    queryKey: storyQueryKeys.property.forestContext(
      propertyId ?? "",
      bufferMeters,
      options.omitAuthorization ? "public" : "credentialed",
    ),
    enabled: Boolean(propertyId) && enabled,
    staleTime: 30_000,
    // Do not retry this one. It carries a 90 s budget because a property's
    // first read enriches every stand from the MML elevation model
    // and React Query's default retry of 3 would spend
    // that budget four times: ~6 minutes before the owner sees anything, and
    // four full cold enrichment passes from a single tab — the exact burst the
    // provider bound exists to prevent. A timeout here means the backend is
    // already the bottleneck, which is the same reason
    // `http_clients.retry_read_transport` refuses to retry a read timeout.
    retry: false,
    refetchOnMount: options.refetchOnMount,
    queryFn: () => getPropertyForestContext(propertyId!, bufferMeters, undefined, {
      omitAuthorization: options.omitAuthorization === true,
    }),
  });

  useEffect(() => {
    if (!query.error) return;
    logger.error(
      {
        component: "usePropertyForestContext",
        propertyId: propertyId ?? undefined,
        action: "fetch",
      },
      "Failed to load property forest context",
      query.error,
    );
  }, [propertyId, query.error]);

  return {
    context: query.data ?? null,
    isLoading: query.isFetching,
    isStale: query.isStale,
    error: query.error ? "failed" : null,
    refetch: async () => {
      await query.refetch();
    },
  };
}
